import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Beaker, ArrowRight, Zap, Shield, BarChart3, FileText, Bot, Clock,
  Users, BookOpen, GitBranch, Linkedin, Globe, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } },
};

export default function AboutPage() {
  const [team, setTeam] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const res = await axios.get(`${API_BASE}/public/team`);
      setTeam(res.data.members || []);
    } catch (e) { console.error(e); }
  };

  const roleColors = {
    founder: 'bg-primary/15 text-primary border-primary/30',
    lead: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
    contributor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    advisor: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  };

  return (
    <div className="">
      {/* Nav */}

      {/* Hero */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-[800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 mb-4">
              <Globe className="w-3 h-3" /> Open Scientific Platform
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-[-0.02em]" style={{ fontFamily: 'Space Grotesk' }}>
              About Pharmax.ai
            </h1>
            <p className="text-lg text-muted-foreground mt-6 leading-relaxed">
              An AI-first pharmacometrics platform built by pharmacometricians, for pharmacometricians.
              We're replacing the fragmented 5-10 tool workflow with one intelligent system that understands clinical pharmacology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-12 px-6">
        <div className="max-w-[800px] mx-auto space-y-12">
          {[
            { icon: Beaker, title: 'Our Mission',
              text: 'Replace the fragmented pharmacometrics workflow (NONMEM + R + WinNonlin + SAS + Excel + Word) with one AI-powered platform. Every analysis — NCA, PopPK, PBPK, E-R, simulations — guided by specialized AI agents that understand pharmacometric science.' },
            { icon: Shield, title: 'Privacy-First Architecture',
              text: 'Raw patient data never reaches the AI. Our SchemaExtractor creates metadata-only summaries (column names, counts, aggregate stats) before any LLM call. Computation happens locally; only aggregate results are interpreted.' },
            { icon: GitBranch, title: 'Multi-Agent System',
              text: '13 specialized AI agents orchestrated by a Supervisor. Each agent has domain expertise: NCA computation, PopPK modeling, QC diagnostics, regulatory intelligence. 76 workflow templates structure multi-step analyses with human review gates.' },
          ].map((item, i) => (
            <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex gap-5">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{item.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="py-16 px-6 border-t border-border/50">
        <div className="max-w-[1000px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Team</h2>
            <p className="text-muted-foreground mt-2">The scientists and engineers behind Pharmax.ai</p>
          </motion.div>

          <div className="grid gap-6">
            {team.map((member, i) => (
              <motion.div key={member.id || i} initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className={`card-hover overflow-hidden ${member.role === 'founder' ? 'border-primary/30' : ''}`}>
                  <CardContent className={`py-6 px-6 ${member.role === 'founder' ? 'bg-primary/5' : ''}`}>
                    <div className="flex items-start gap-5">
                      {/* Avatar */}
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl font-bold ${
                        member.role === 'founder' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        {member.name?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{member.name}</h3>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${roleColors[member.role] || roleColors.contributor}`}>
                            {member.role}
                          </span>
                        </div>
                        <p className="text-sm text-primary/80 mt-0.5">{member.title}</p>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{member.bio}</p>
                        {member.linkedin_url && (
                          <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-3 text-xs text-primary hover:underline">
                            <Linkedin className="w-3.5 h-3.5" /> LinkedIn Profile
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}

            {team.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Team profiles loading...
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Architecture overview */}
      <section className="py-16 px-6 border-t border-border/50">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8" style={{ fontFamily: 'Space Grotesk' }}>System Architecture</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: '13 AI Agents', desc: 'Supervisor, Data Manager, NCA, PopPK Expert, PKPD Expert, E-R Expert, PBPK, Statistical, Simulator, QC, Report, Regulatory Intel, General', icon: Bot },
              { title: '76 Workflow Templates', desc: 'NCA, PopPK, PKPD, E-R, Special Populations, DDI, Simulation, Precision Dosing, PBPK, Reports, Study Design', icon: BookOpen },
              { title: '24 Regulatory Guidances', desc: 'FDA, EMA, ICH, PMDA, NMPA — searchable knowledge base with RAG vector search', icon: FileText },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className="card-hover h-full">
                  <CardContent className="pt-5 pb-4">
                    <item.icon className="w-8 h-8 text-primary mb-3" />
                    <h3 className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{item.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t border-border/50">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Join the Community</h2>
          <p className="text-muted-foreground mt-2">Try Pharmax.ai free. Contribute to the future of pharmacometrics.</p>
          <div className="flex gap-3 justify-center mt-6">
            <Button onClick={() => navigate('/signup')} className="rounded-full h-11 px-6 text-sm">
              Get Started Free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/architecture')} className="rounded-full h-11 px-6 text-sm">
              View Architecture
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
    </div>
  );
}
